import api from '@/lib/api';

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface FolioItem {
  id: string;
  bookingId: string;
  category: string;
  description: string;
  amount: string;
  currency: string;
  date: string;
  createdBy: string | null;
  createdAt: string;
}

/** A billed section (Room or Food): pre-tax subtotal, tax %, tax, and total. */
export interface FolioSection {
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
}

/** Per-section tax breakdown; grandTotal = room.total + food.total + other. */
export interface FolioTaxBreakdown {
  room: FolioSection;
  food: FolioSection;
  other: number;
  grandTotal: number;
}

export interface FolioSummary {
  bookingId: string;
  items: FolioItem[];
  totalCharges: number;
  totalPayments: number;
  balance: number;
  currency: string;
  /** Present on normal single-currency folios; drives the tax lines in Bills. */
  tax?: FolioTaxBreakdown;
}

export interface RecordPaymentPayload {
  bookingId: string;
  amount: number;
  paymentMethod: 'cash' | 'card' | 'bank_transfer' | 'other';
  reference?: string;
  notes?: string;
}

export interface AddFolioItemPayload {
  category?: string;
  description: string;
  amount?: number;
  date: string;
  // Optional Charge Type link (TASK-68, Flow 1). When set, the backend prefills
  // category/amount/taxable from the active charge type.
  chargeTypeId?: string;
}

// ─── API functions ───────────────────────────────────────────────────────────

export async function fetchFolioSummary(bookingId: string): Promise<FolioSummary> {
  const { data } = await api.get<FolioSummary>(`/payments/folio/${bookingId}/summary`);
  return data;
}

export async function addFolioItem(bookingId: string, payload: AddFolioItemPayload): Promise<FolioItem> {
  const { data } = await api.post<FolioItem>(`/payments/folio/${bookingId}`, payload);
  return data;
}

export async function removeFolioItem(itemId: string): Promise<void> {
  await api.delete(`/payments/folio/items/${itemId}`);
}

// ─── Invoice generation + PDF download ───────────────────────────────────────

export interface GuestInvoice {
  id: string;
  invoiceNumber: string;
  invoiceType: string;
  grossAmount: string;
  netAmount: string;
  status: string;
  issuedAt: string;
}

/** Generate (or re-generate) a guest invoice for a booking. */
export async function generateGuestInvoice(bookingId: string): Promise<GuestInvoice> {
  // PaymentsController is @Controller() with no prefix — invoice routes are at /invoices/...
  const { data } = await api.post<GuestInvoice>(`/invoices/guest/${bookingId}`);
  return data;
}

/**
 * Fetch the PDF blob for an invoice.
 * Returns a blob URL that the caller is responsible for revoking.
 */
export async function fetchInvoiceBlobUrl(invoiceId: string): Promise<string> {
  const response = await api.get(`/invoices/${invoiceId}/pdf`, {
    responseType: 'blob',
  });
  // response.data is already a Blob when responseType='blob' — no need to re-wrap it
  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data as BlobPart], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
}

/** Generate invoice then return a blob URL for embedding. */
export async function generateAndFetchBill(bookingId: string): Promise<{ blobUrl: string; invoice: GuestInvoice }> {
  const invoice = await generateGuestInvoice(bookingId);
  const blobUrl = await fetchInvoiceBlobUrl(invoice.id);
  return { blobUrl, invoice };
}

/** Legacy: generate then open in new tab (kept as print fallback). */
export async function printBillForBooking(bookingId: string): Promise<void> {
  const { blobUrl, invoice } = await generateAndFetchBill(bookingId);
  const win = window.open(blobUrl, '_blank');
  if (!win) {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `invoice-${invoice.invoiceNumber}.pdf`;
    a.click();
  }
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
}

export async function recordPayment(payload: RecordPaymentPayload): Promise<unknown> {
  const { data } = await api.post('/payments/record', payload);
  return data;
}
